// Imposter Join Page
// Player interface for joining and playing Imposter game

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ImposterPlayerRecord,
  subscribeToImposterPlayers,
  subscribeToImposterGameState,
  upsertImposterPlayer,
  setPlayerReady,
  renameImposterPlayer,
  leaveImposterQueue,
  markClueSubmitted,
  markPlayerVoted,
  setPlayerVotePreference,
} from '../games/imposter/lobby';
import { ImposterStateDocument, getPlayerView } from '../games/imposter/logic';
import { submitClue, submitVote } from '../games/imposter/service';

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `player_${Math.random().toString(36).slice(2, 10)}`;
};

const storageKey = (partyId: string) => `qroke:imposter:player:${partyId}`;

const ImposterJoinPage: React.FC = () => {
  const { partyId } = useParams<{ partyId: string }>();
  const navigate = useNavigate();

  const [playerId, setPlayerId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [playerDoc, setPlayerDoc] = useState<ImposterPlayerRecord | null>(null);
  const [players, setPlayers] = useState<ImposterPlayerRecord[]>([]);
  const [gameState, setGameState] = useState<ImposterStateDocument | null>(null);
  const [statusMessage, setStatusMessage] = useState('Enter your name to join the game.');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Input states
  const [clueInput, setClueInput] = useState('');
  const [selectedSuspect, setSelectedSuspect] = useState<string | null>(null);

  // Load player ID from localStorage
  useEffect(() => {
    if (!partyId) return;
    const stored = localStorage.getItem(storageKey(partyId));
    if (stored) {
      setPlayerId(stored);
    }
  }, [partyId]);

  // Subscribe to all players
  useEffect(() => {
    if (!partyId) return;
    const unsubscribe = subscribeToImposterPlayers(partyId, setPlayers);
    return () => unsubscribe();
  }, [partyId]);

  // Subscribe to game state
  useEffect(() => {
    if (!partyId) return;
    const unsubscribe = subscribeToImposterGameState(partyId, setGameState);
    return () => unsubscribe();
  }, [partyId]);

  // Find current player doc
  useEffect(() => {
    if (!playerId) return;
    const doc = players.find(p => p.id === playerId);
    setPlayerDoc(doc || null);
    if (doc && !name) {
      setName(doc.name);
    }
  }, [playerId, players, name]);

  // Update status message
  useEffect(() => {
    if (!playerDoc) {
      setStatusMessage('Enter your name to join the game.');
    } else if (!gameState) {
      setStatusMessage('Waiting for the host to start the game...');
    } else if (gameState.phase === 'dealing') {
      const isImposter = gameState.imposterIds.includes(playerDoc.name);
      setStatusMessage(
        isImposter
          ? '🎭 You are the IMPOSTER! You don\'t know the word. Blend in!'
          : `📝 The secret word is: ${gameState.secretWord}`
      );
    } else if (gameState.phase === 'clues') {
      const currentPlayer = gameState.players[gameState.currentPlayerIndex];
      if (currentPlayer === playerDoc.name) {
        setStatusMessage('It\'s your turn to give a clue!');
      } else {
        setStatusMessage(`Waiting for ${currentPlayer} to give a clue...`);
      }
    } else if (gameState.phase === 'voting') {
      setStatusMessage('Vote for who you think is the imposter!');
    } else if (gameState.phase === 'reveal') {
      setStatusMessage('Revealing results...');
    } else if (gameState.phase === 'finished') {
      setStatusMessage('🎉 Round finished! Get ready for another round...');
    }
  }, [playerDoc, gameState]);

  const ensurePlayerId = () => {
    if (playerId) return playerId;
    const generated = generateId();
    setPlayerId(generated);
    if (partyId) {
      try {
        localStorage.setItem(storageKey(partyId), generated);
      } catch {}
    }
    return generated;
  };

  const handleJoin = useCallback(async () => {
    if (!partyId || !name.trim()) {
      setError('Enter your name first.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const pid = ensurePlayerId();
      await upsertImposterPlayer(partyId, pid, name.trim(), 'waiting');
      setStatusMessage('You\'re in! Waiting for game to start...');
    } catch (err: any) {
      setError(err.message || 'Failed to join');
      console.error('Error joining:', err);
    } finally {
      setSaving(false);
    }
  }, [partyId, name]);

  const handleReady = useCallback(async () => {
    if (!partyId || !playerId) return;

    setSaving(true);
    setError(null);

    try {
      await setPlayerReady(partyId, playerId, true);
    } catch (err: any) {
      setError(err.message || 'Failed to ready up');
      console.error('Error readying:', err);
    } finally {
      setSaving(false);
    }
  }, [partyId, playerId]);

  const handleSubmitClue = useCallback(async () => {
    if (!partyId || !playerDoc || !clueInput.trim()) return;

    setSaving(true);
    setError(null);

    try {
      await submitClue(partyId, playerDoc.name, clueInput.trim());
      await markClueSubmitted(partyId, playerId!);
      setClueInput('');
      setStatusMessage('Clue submitted! Waiting for other players...');
    } catch (err: any) {
      setError(err.message || 'Failed to submit clue');
      console.error('Error submitting clue:', err);
    } finally {
      setSaving(false);
    }
  }, [partyId, playerDoc, clueInput, playerId]);

  const handleVote = useCallback(async (suspectName: string) => {
    if (!partyId || !playerDoc) return;

    setSaving(true);
    setError(null);

    try {
      await submitVote(partyId, playerDoc.name, suspectName);
      await markPlayerVoted(partyId, playerId!);
      setSelectedSuspect(suspectName);
      setStatusMessage('Vote submitted!');
    } catch (err: any) {
      setError(err.message || 'Failed to vote');
      console.error('Error voting:', err);
    } finally {
      setSaving(false);
    }
  }, [partyId, playerDoc, playerId]);

  const handleVotePreference = useCallback(async (wantsToVote: boolean) => {
    if (!partyId || !playerId) return;

    setSaving(true);
    setError(null);

    try {
      await setPlayerVotePreference(partyId, playerId, wantsToVote);
      setStatusMessage(wantsToVote ? 'You voted to start voting!' : 'You voted to continue!');
    } catch (err: any) {
      setError(err.message || 'Failed to set preference');
      console.error('Error setting vote preference:', err);
    } finally {
      setSaving(false);
    }
  }, [partyId, playerId]);

  const handleRename = useCallback(async (newName: string) => {
    if (!partyId || !playerId || !newName.trim()) return;

    setSaving(true);
    setError(null);

    try {
      await renameImposterPlayer(partyId, playerId, newName.trim());
      setName(newName.trim());
    } catch (err: any) {
      setError(err.message || 'Failed to rename');
      console.error('Error renaming:', err);
    } finally {
      setSaving(false);
    }
  }, [partyId, playerId]);

  const handleLeave = useCallback(async () => {
    if (!partyId || !playerId) return;

    try {
      await leaveImposterQueue(partyId, playerId);
      localStorage.removeItem(storageKey(partyId));
      navigate(`/party/${partyId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to leave');
      console.error('Error leaving:', err);
    }
  }, [partyId, playerId, navigate]);

  // Get player view (hides secret word from imposter)
  const playerView = useMemo(() => {
    if (!gameState || !playerDoc) return null;
    return getPlayerView(gameState, playerDoc.name);
  }, [gameState, playerDoc]);

  const isImposter = useMemo(() => {
    if (!gameState || !playerDoc) return false;
    return gameState.imposterIds.includes(playerDoc.name);
  }, [gameState, playerDoc]);

  const isMyTurn = useMemo(() => {
    if (!gameState || !playerDoc || gameState.phase !== 'clues') return false;
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    return currentPlayer === playerDoc.name;
  }, [gameState, playerDoc]);

  const sortedScores = useMemo(() => {
    if (!gameState) return [];
    return Object.entries(gameState.scores)
      .map(([name, score]) => ({ name, score }))
      .sort((a, b) => b.score - a.score);
  }, [gameState]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 text-white p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-purple-200 mb-2">🎭 Imposter</h1>
          <p className="text-sm text-purple-300">{statusMessage}</p>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 mb-4">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {/* Join Interface */}
        {!playerDoc && (
          <div className="bg-white/10 rounded-xl p-6 border border-purple-400/30">
            <h2 className="text-2xl font-bold mb-4">Join the Game</h2>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleJoin()}
              placeholder="Enter your name..."
              className="w-full bg-black/30 border border-purple-400/30 rounded-lg px-4 py-3 text-white text-lg mb-4"
            />
            <button
              onClick={handleJoin}
              disabled={saving || !name.trim()}
              className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-bold text-lg"
            >
              {saving ? 'Joining...' : 'Join Game'}
            </button>
          </div>
        )}

        {/* Player is in lobby */}
        {playerDoc && !gameState && (
          <div className="bg-white/10 rounded-xl p-6 border border-purple-400/30">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Welcome, {playerDoc.name}!</h2>
              <button
                onClick={handleLeave}
                className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded-lg text-sm"
              >
                Leave
              </button>
            </div>
            <p className="text-purple-300 mb-4">
              Waiting for the host to start the game...
            </p>
            <div className="bg-indigo-900/40 rounded-lg p-4">
              <p className="text-sm text-purple-300 mb-2">Players in lobby:</p>
              <div className="space-y-2">
                {players.filter(p => p.status !== 'left').map(p => (
                  <div
                    key={p.id}
                    className="flex justify-between items-center bg-purple-900/30 rounded p-2"
                  >
                    <span>{p.name}</span>
                    {p.isReady && (
                      <span className="text-xs bg-green-500/30 px-2 py-1 rounded">
                        Ready
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Game is active */}
        {playerDoc && gameState && (
          <div className="space-y-4">
            {/* Role Info */}
            {gameState.phase === 'dealing' && (
              <div className={`rounded-xl p-6 border ${
                isImposter
                  ? 'bg-red-900/40 border-red-400/50'
                  : 'bg-green-900/40 border-green-400/50'
              }`}>
                <h2 className="text-3xl font-bold mb-2">
                  {isImposter ? '🎭 You are the IMPOSTER!' : '📝 You know the word!'}
                </h2>
                {!isImposter && (
                  <p className="text-2xl font-bold text-yellow-300">
                    Secret Word: {playerView?.secretWord}
                  </p>
                )}
                {isImposter && (
                  <p className="text-lg text-red-200">
                    You don't know the secret word. Listen carefully to blend in!
                  </p>
                )}
                <p className="text-sm text-gray-300 mt-2">Category: {gameState.category}</p>
              </div>
            )}

            {/* Clue Input */}
            {gameState.phase === 'clues' && isMyTurn && !playerDoc.hasSubmittedClue && (
              <div className="bg-blue-900/40 rounded-xl p-6 border border-blue-400/50">
                <h2 className="text-2xl font-bold mb-4">Give a Clue!</h2>
                <p className="text-sm text-blue-200 mb-4">
                  Give a one-word clue that describes the word (or blend in if you're the imposter!)
                </p>
                <input
                  type="text"
                  value={clueInput}
                  onChange={e => setClueInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmitClue()}
                  placeholder="Your clue..."
                  className="w-full bg-black/30 border border-blue-400/30 rounded-lg px-4 py-3 text-white text-lg mb-4"
                />
                <button
                  onClick={handleSubmitClue}
                  disabled={saving || !clueInput.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-6 py-3 rounded-lg font-bold"
                >
                  {saving ? 'Submitting...' : 'Submit Clue'}
                </button>
              </div>
            )}

            {/* Waiting for turn */}
            {gameState.phase === 'clues' && !isMyTurn && (
              <div className="bg-slate-900/40 rounded-xl p-6 border border-slate-400/30">
                <p className="text-center text-lg">
                  Waiting for {gameState.players[gameState.currentPlayerIndex]} to give a clue...
                </p>
                {playerDoc.hasSubmittedClue && (
                  <p className="text-center text-sm text-green-400 mt-2">
                    ✓ You've submitted your clue
                  </p>
                )}
              </div>
            )}

            {/* Voting Poll - Show after player submits clue */}
            {gameState.phase === 'clues' && playerDoc.hasSubmittedClue && !isMyTurn && playerDoc.wantsToVote === undefined && (
              <div className="bg-orange-900/40 rounded-xl p-6 border border-orange-400/50">
                <h2 className="text-2xl font-bold mb-4 text-center">🗳️ What's Next?</h2>
                <p className="text-sm text-orange-200 mb-4 text-center">
                  Do you want to continue with more clues or vote for the imposter now?
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => handleVotePreference(false)}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-6 py-4 rounded-lg font-bold"
                  >
                    <div className="text-2xl mb-1">▶️</div>
                    <div>Continue</div>
                  </button>
                  <button
                    onClick={() => handleVotePreference(true)}
                    disabled={saving}
                    className="bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 px-6 py-4 rounded-lg font-bold"
                  >
                    <div className="text-2xl mb-1">🗳️</div>
                    <div>Vote Now</div>
                  </button>
                </div>
              </div>
            )}

            {/* Show preference after they've chosen */}
            {gameState.phase === 'clues' && playerDoc.hasSubmittedClue && !isMyTurn && playerDoc.wantsToVote !== undefined && (
              <div className="bg-green-900/40 rounded-xl p-4 border border-green-400/50">
                <p className="text-center text-sm">
                  {playerDoc.wantsToVote ? '✓ You want to vote now' : '✓ You want to continue'}
                </p>
                <p className="text-center text-xs text-green-300 mt-1">
                  Waiting for other players to decide...
                </p>
              </div>
            )}

            {/* Voting */}
            {gameState.phase === 'voting' && !playerDoc.hasVoted && (
              <div className="bg-yellow-900/40 rounded-xl p-6 border border-yellow-400/50">
                <h2 className="text-2xl font-bold mb-4">Vote for the Imposter!</h2>
                <p className="text-sm text-yellow-200 mb-4">
                  Who do you think is the imposter?
                </p>
                <div className="space-y-2">
                  {gameState.players.map(player => (
                    <button
                      key={player}
                      onClick={() => handleVote(player)}
                      disabled={saving || player === playerDoc.name}
                      className={`w-full px-4 py-3 rounded-lg font-semibold ${
                        player === playerDoc.name
                          ? 'bg-gray-700 cursor-not-allowed text-gray-400'
                          : 'bg-yellow-600 hover:bg-yellow-500'
                      }`}
                    >
                      {player} {player === playerDoc.name && '(You)'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Already voted */}
            {gameState.phase === 'voting' && playerDoc.hasVoted && (
              <div className="bg-slate-900/40 rounded-xl p-6 border border-slate-400/30">
                <p className="text-center text-lg">
                  ✓ You voted for {selectedSuspect}
                </p>
                <p className="text-center text-sm text-gray-400 mt-2">
                  Waiting for other players to vote...
                </p>
              </div>
            )}

            {/* Reveal Phase */}
            {(gameState.phase === 'reveal' || gameState.phase === 'finished') && (
              <div className="bg-purple-900/40 rounded-xl p-6 border border-purple-400/50">
                <h2 className="text-2xl font-bold mb-4">Results</h2>
                
                <div className="bg-indigo-900/40 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-300">Secret Word</p>
                  <p className="text-3xl font-bold text-yellow-300">{gameState.secretWord}</p>
                </div>

                <div className="bg-red-900/40 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-300 mb-2">Imposter(s)</p>
                  {gameState.imposterIds.map(id => (
                    <p key={id} className="text-xl font-bold text-red-200">{id}</p>
                  ))}
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-purple-300">Scores</h3>
                  {sortedScores.map((entry, idx) => (
                    <div
                      key={entry.name}
                      className={`rounded-lg p-3 ${
                        idx === 0
                          ? 'bg-yellow-900/40 border border-yellow-400/50'
                          : 'bg-slate-900/40'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">
                          {idx + 1}. {entry.name}
                        </span>
                        <span className="text-2xl font-bold">{entry.score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Clues Display */}
            {gameState.clues.length > 0 && (
              <div className="bg-white/10 rounded-xl p-6 border border-purple-400/30">
                <h3 className="text-xl font-bold mb-4">Clues</h3>
                <div className="space-y-2">
                  {gameState.clues.map((clue, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-900/40 rounded-lg p-3 border border-slate-400/30"
                    >
                      <p className="text-xs text-slate-400">
                        Round {clue.roundNumber} - {clue.playerId}
                      </p>
                      <p className="text-lg font-semibold text-purple-200">
                        "{clue.clue}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leave button */}
            <button
              onClick={handleLeave}
              className="w-full bg-red-600 hover:bg-red-500 px-4 py-2 rounded-lg"
            >
              Leave Game
            </button>
          </div>
        )}
      </div>

      {/* Mobile Navigation Footer */}
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden">
        <div className="bg-black/70 backdrop-blur border-t border-white/10 text-white">
          <div
            className="flex items-stretch justify-around py-2"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}
          >
            <button
              onClick={() => navigate(`/party/${partyId}/game/imposter`)}
              className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
              aria-label="Back to Host"
            >
              <div className="text-lg">🖥️</div>
              <div className="text-xs">Host View</div>
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
              aria-label="Dashboard"
            >
              <div className="text-lg">🎮</div>
              <div className="text-xs">Games</div>
            </button>
            <button
              onClick={handleLeave}
              className="flex-1 text-center px-3 py-2 hover:bg-white/5 active:bg-white/10 rounded"
              aria-label="Leave Game"
            >
              <div className="text-lg">🚪</div>
              <div className="text-xs">Leave</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImposterJoinPage;
